/**
 * Attempts to parse a JSON string. If the initial parsing fails, it attempts to
 * "repair" common malformations in the JSON string (e.g., single quotes, unquoted keys)
 * and then tries to parse it again.
 *
 * This function is useful for handling JSON strings that might be slightly
 * malformed but are intended to be valid JSON.
 *
 * @param source The string to be parsed as JSON.
 * @returns The parsed JavaScript object/value if successful, or `undefined` if parsing
 * fails even after repair attempts. The return type is `unknown` because
 * the parsed JSON could be any valid JSON type (object, array, string, number, boolean, null).
 * @example
 * // Valid JSON
 * parseJSON('{"name": "Alice", "age": 30}'); // { name: "Alice", age: 30 }
 *
 * @example
 * // JSON with single quotes (will be repaired)
 * parseJSON("{'name': 'Bob', 'city': 'New York'}"); // { name: "Bob", city: "New York" }
 *
 * @example
 * // JSON with unquoted keys (will be repaired)
 * parseJSON('{name: "Charlie", "occupation": "Engineer"}'); // { "name": "Charlie", "occupation": "Engineer" }
 *
 * @example
 * // Invalid JSON (will return undefined)
 * parseJSON('{"name": "David", age:'); // undefined
 */
export const parseJSON = <T = unknown>(source?: string): T | undefined => {
  if (!source || typeof source !== 'string') {
    return;
  }

  try {
    // Attempt to parse the source string as JSON
    return JSON.parse(source);

  } catch {
    try {
      // Attempt to repair malformed JSON:
      // 1. Replace single quotes with double quotes
      // 2. Wrap unquoted keys in double quotes (e.g., {key: "value"} -> {"key": "value"})
      const repaired = source
        .replace(/'/g, '"')
        .replace(/([{,]\s*)(\w+):/g, '$1"$2":'); // Robust regex for unquoted keys

      return JSON.parse(repaired);
    } catch (e2) {
      console.error('Failed to parse JSON after multiple attempts:', e2);
      return; // Return undefined if repair also fails
    }
  }
};
